<script lang="ts" setup>
import { ref } from 'vue'
import RoomieActionMenu from '@lahaus-roomie/roomie/src/components/RoomieActionMenu/index.vue'
import RoomieNavigationButton from '@lahaus-roomie/roomie/src/components/RoomieNavigationButton/index.vue'
import RoomieIcon from '@lahaus-roomie/roomie/src/components/RoomieIcon/index.vue'
import PinIcon from '@lahaus-roomie/roomie/src/assets/icons/pin.svg'
import EmailIcon from '@lahaus-roomie/roomie/src/assets/icons/email.svg'
import PhoneIcon from '@lahaus-roomie/roomie/src/assets/icons/phone.svg'
import LaHausLogo from '@lahaus-roomie/roomie/src/assets/icons/lahaus-logo.svg'
import LaHausLogoV2 from '@lahaus-roomie/roomie/src/assets/icons/v2/la-haus.svg'
import YoutubeIcon from '@lahaus-roomie/roomie/src/assets/icons/youtube-filled.svg'
import TwitterIcon from '@lahaus-roomie/roomie/src/assets/icons/twitter-filled.svg'
import TiktokIcon from '@lahaus-roomie/roomie/src/assets/icons/tiktok-filled.svg'
import InstagramIcon from '@lahaus-roomie/roomie/src/assets/icons/instagram-filled.svg'
import LinkedinIcon from '@lahaus-roomie/roomie/src/assets/icons/linkedin-filled.svg'
import FacebookIcon from '@lahaus-roomie/roomie/src/assets/icons/facebook-filled.svg'

const props = defineProps({
  countries: {
    type: Array as () => any[],
    required: true
  },
  socialNetworks: {
    type: Array as () => any[],
    required: true
  },
  copyrightText: {
    type: String,
    default: ''
  }
})

const selectedCountry = ref(props.countries[0])
const selectedCity = ref(props.countries[0].cities[0])

const toggleCountry = (country: any) => {
  selectedCountry.value = country
  selectedCity.value = country.cities[0]
}

const toggleCity = (city: any) => {
  selectedCity.value = city
}

const getCurrentYear = () => new Date().getFullYear()

const redirectTo = (link: string) => {
  const a = document.createElement('a')
  a.href = link
  a.setAttribute('target', '_blank')
  a.click()
}
</script>

<script lang="ts">
export default {
  name: 'AppFooter'
}
</script>

<template>
  <footer class="roomie-footer">
    <div class="w-full flex flex-col justify-center items-center">
      <div class="w-full roomie-footer__top max-w-screen-xl">
        <div class="roomie-footer__container mx-auto justify-between items-center">
          <div class="roomie-footer__logo-v2">
            <RoomieIcon custom-styles="block text-pine-600 w-[15rem] h-34">
              <LaHausLogoV2 />
            </RoomieIcon>
          </div>

          <div class="roomie-footer__logo-v1">
            <LaHausLogo />
          </div>

          <RoomieActionMenu
            :items="countries"
            :default-choice="selectedCountry"
            class="roomie-action-menu-countries"
            @toggle-action="toggleCountry" />
        </div>
      </div>

      <div class="w-full roomie-footer__center max-w-screen-xl">
        <div class="roomie-footer__container mx-auto w-full justify-between">
          <div class="roomie-footer__content">
            <div class="roomie-footer__contact">
              <div class="roomie-footer__contact-dropdown">
                <span class="roomie-footer__contact-title">Contacto en:</span>

                <RoomieActionMenu
                  :items="selectedCountry.cities"
                  :default-choice="selectedCity"
                  @toggle-action="toggleCity" />
              </div>

              <ul class="roomie-footer__contact-menu">
                <li>
                  <div>
                    <PinIcon class="roomie-icon text-brand-800 pointer-events-none" />
                  </div>

                  <a
                    :href="`http://maps.google.com/?q=${selectedCity.address}`"
                    target="_blank"
                    rel="noopener noreferrer">
                    {{ selectedCity.address }}
                  </a>
                </li>

                <li>
                  <EmailIcon class="roomie-icon text-brand-800 pointer-events-none" />

                  <a href="mailto:servicioalcliente@lahaus.com">servicioalcliente@lahaus.com</a>
                </li>

                <li v-if="selectedCity.phone">
                  <PhoneIcon class="roomie-icon text-brand-800 pointer-events-none" />

                  <a
                    :href="`https://wa.me/${selectedCity.phone}`"
                    target="_blank"
                    rel="noopener noreferrer">
                    +{{ selectedCity.phone }}
                  </a>
                </li>

                <li v-if="selectedCity.availability">
                  <p class="roomie-footer__description-content">
                    {{ selectedCity.availability }}
                  </p>
                </li>
              </ul>
            </div>
          </div>

          <div class="roomie-footer__navigation">
            <div
              v-for="navLink in selectedCountry.footerNavigation"
              :key="navLink.id"
              class="roomie-footer__navigation-row">
              <h4 class="roomie-footer__navigation-title">
                {{ navLink.title }}
              </h4>

              <ul>
                <li
                  v-for="element in navLink.items"
                  :key="element.id">
                  <a
                    :href="element.link"
                    rel="noopener noreferrer">
                    {{ element.description }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="roomie-footer__bottom">
      <div class="roomie-footer__container mx-auto justify-between items-center">
        <div class="roomie-footer__social-icons">
          <RoomieNavigationButton
            v-for="socialNetwork in socialNetworks"
            :id="socialNetwork.name"
            :key="socialNetwork.id"
            variant="dark"
            :aria-label="socialNetwork.name"
            @click="redirectTo(socialNetwork.link)">
            <YoutubeIcon v-if="socialNetwork.name === 'youtube'" />

            <TwitterIcon v-if="socialNetwork.name === 'twitter'" />

            <TiktokIcon v-if="socialNetwork.name === 'tiktok'" />

            <InstagramIcon v-if="socialNetwork.name === 'instagram'" />

            <LinkedinIcon v-if="socialNetwork.name === 'linkedin'" />

            <FacebookIcon v-if="socialNetwork.name === 'facebook'" />
          </RoomieNavigationButton>
        </div>

        <div class="roomie-footer__terms">
          <p class="roomie-footer__terms-copyright">
            © {{ getCurrentYear() }} {{ copyrightText }}
          </p>

          <ul class="roomie-footer__bottom-links">
            <li
              v-for="item in selectedCountry.footerTerms"
              :key="item.id">
              <a
                class="roomie-footer__bottom-link"
                :href="item.link"
                rel="noopener noreferrer">
                {{ item.description }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </footer>
</template>

<style src='@lahaus-roomie/roomie/src/components/RoomieFooter/_index.scss' lang='scss'/>
